import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PRESTATIONS, ENERGIES, getReferenceConfig } from "@/lib/constants";

const inputCls =
  "w-full bg-[#0A1628] border border-[#2C4066] rounded px-4 py-3 text-sm text-white focus:outline-none focus:border-[#51ADDA] placeholder:text-slate-600";
const labelCls = "block font-mono text-xs uppercase tracking-wider text-slate-300 mb-2";

const EMPTY = {
  ecu_reference: "",
  vehicle: "",
  year: "",
  motorisation: "",
  energie: "Diesel",
  puissance: "",
  prestation: PRESTATIONS[0],
  email: "",
  telephone: "",
};

const SuccessPanel = ({ reference, email, onReset }) => (
  <div data-testid="devis-success-panel" className="bg-[#0F1D36] border border-[#1C2C4E] rounded-xl p-8 md:p-12 shadow-2xl text-center">
    <span className="font-mono text-xs uppercase tracking-[0.14em] text-emerald-400 block mb-4">Demande bien reçue</span>
    <h2 className="font-display text-3xl md:text-4xl uppercase tracking-tight text-white mb-6">Votre référence de suivi</h2>
    <div data-testid="devis-reference-value" className="font-display text-4xl sm:text-5xl md:text-6xl text-[#51ADDA] tracking-wider mb-6 break-all">{reference}</div>
    <p className="text-slate-400 text-sm max-w-md mx-auto mb-8">
      Conservez cette référence : elle vous permettra de suivre votre demande et de payer votre devis en ligne.
      Notre réponse chiffrée arrive sous 2h ouvrées.
    </p>
    <div className="flex flex-wrap justify-center gap-4">
      <button
        type="button"
        data-testid="devis-copy-button"
        onClick={() => {
          navigator.clipboard.writeText(reference);
          toast.success("Référence copiée");
        }}
        className="px-6 py-3 rounded border border-[#2C4066] text-xs font-mono uppercase tracking-wider text-slate-300 hover:border-[#51ADDA] hover:text-white transition-colors"
      >
        Copier la référence
      </button>
      <Link
        to={`/suivi?reference=${reference}&email=${encodeURIComponent(email)}`}
        data-testid="devis-suivi-link"
        className="px-6 py-3 rounded bg-[#51ADDA] text-[#0A1628] text-xs font-mono uppercase tracking-wider font-bold hover:bg-white transition-colors"
      >
        Suivre ma demande
      </Link>
      <button
        type="button"
        data-testid="devis-new-request-button"
        onClick={onReset}
        className="px-6 py-3 rounded text-xs font-mono uppercase tracking-wider text-slate-500 hover:text-white transition-colors"
      >
        Nouvelle demande
      </button>
    </div>
  </div>
);

export const DevisForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reference, setReference] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const refCfg = getReferenceConfig(form.prestation);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Photo trop lourde (5 Mo max)");
      return;
    }
    setPhoto(f);
    setPhotoPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      let photo_path = "";
      if (photo) {
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(photo);
        });
        const up = await api.post("/devis/photo", {
          filename: photo.name || "photo.jpg",
          content_type: photo.type || "image/jpeg",
          data_base64: String(dataUrl).split(",")[1],
        });
        photo_path = up.data.path;
      }
      const r = await api.post("/devis", { ...form, photo_path });
      setReference(r.data.reference);
      toast.success("Demande de devis envoyée !");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="devis" className="py-24 md:py-32 px-6 border-b border-[#1C2C4E] bg-[#06101F]">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <span className="font-mono text-xs uppercase tracking-[0.14em] text-[#51ADDA] block mb-2">Devis Gratuit & Paiement par Carte</span>
          <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tight text-white mb-4">Renseignez Votre Calculateur</h2>
          <p className="text-slate-400 text-sm max-w-lg mx-auto">
            Recevez votre devis détaillé en moins de 2 heures à l'adresse <span className="text-[#51ADDA] font-mono">bellahcen.youness@gmail.com</span>. Vous validerez ensuite le règlement par carte bancaire avant envoi postal.
          </p>
        </div>

        {reference ? (
          <SuccessPanel reference={reference} email={form.email} onReset={() => { setForm(EMPTY); setReference(null); }} />
        ) : (
          <form onSubmit={submit} data-testid="devis-form" className="bg-[#0F1D36] border border-[#1C2C4E] rounded-xl p-8 md:p-12 shadow-2xl space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="prestation" className={labelCls}>Prestation Souhaitée</label>
                <select id="prestation" data-testid="devis-prestation-select" value={form.prestation} onChange={set("prestation")} className={inputCls}>
                  {PRESTATIONS.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="ecu-ref" className={labelCls}>{refCfg.label}</label>
                <input id="ecu-ref" data-testid="devis-ecu-ref-input" type="text" required value={form.ecu_reference} onChange={set("ecu_reference")} placeholder={refCfg.placeholder} className={inputCls} />
              </div>
              <div>
                <label htmlFor="vehicle" className={labelCls}>Marque & Modèle du Véhicule</label>
                <input id="vehicle" data-testid="devis-vehicle-input" type="text" required value={form.vehicle} onChange={set("vehicle")} placeholder="Peugeot 308, VW Golf 7, BMW..." className={inputCls} />
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <label htmlFor="year" className={labelCls}>Année Véhicule</label>
                <input id="year" data-testid="devis-year-input" type="text" required inputMode="numeric" maxLength={4} value={form.year} onChange={set("year")} placeholder="2016" className={inputCls} />
              </div>
              <div>
                <label htmlFor="motorisation" className={labelCls}>Motorisation</label>
                <input id="motorisation" data-testid="devis-motorisation-input" type="text" required value={form.motorisation} onChange={set("motorisation")} placeholder="2.0 HDi" className={inputCls} />
              </div>
              <div>
                <label htmlFor="energie" className={labelCls}>Énergie</label>
                <select id="energie" data-testid="devis-energie-select" value={form.energie} onChange={set("energie")} className={inputCls}>
                  {ENERGIES.map((e2) => (
                    <option key={e2} value={e2}>{e2}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-2 md:col-span-1">
                <label htmlFor="puissance" className={labelCls}>Puissance (Ch)</label>
                <input id="puissance" data-testid="devis-puissance-input" type="text" value={form.puissance} onChange={set("puissance")} placeholder="150 ch" className={inputCls} />
              </div>
            </div>

            <div>
              <label htmlFor="photo" className={labelCls}>Photo du boîtier (optionnel — appareil photo ou galerie)</label>
              <div className="flex items-center gap-4">
                <label
                  htmlFor="photo"
                  data-testid="devis-photo-label"
                  className="cursor-pointer px-4 py-3 rounded border border-dashed border-[#2C4066] text-xs font-mono uppercase tracking-wider text-slate-300 hover:border-[#51ADDA] hover:text-white transition-colors"
                >
                  {photo ? "Changer la photo" : "Ajouter une photo"}
                </label>
                <input id="photo" data-testid="devis-photo-input" type="file" accept="image/*" capture="environment" onChange={onPhoto} className="hidden" />
                {photoPreview && (
                  <div className="flex items-center gap-3">
                    <img src={photoPreview} alt="Aperçu du boîtier" data-testid="devis-photo-preview" className="h-14 w-14 object-cover rounded border border-[#2C4066]" />
                    <button
                      type="button"
                      data-testid="devis-photo-remove"
                      onClick={() => { setPhoto(null); setPhotoPreview(null); }}
                      className="text-xs font-mono uppercase tracking-wider text-red-400 hover:text-red-300"
                    >
                      Retirer
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="email" className={labelCls}>Votre Email</label>
                <input id="email" data-testid="devis-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="contact@garage.fr" className={inputCls} />
              </div>
              <div>
                <label htmlFor="telephone" className={labelCls}>Votre Téléphone (optionnel)</label>
                <input id="telephone" data-testid="devis-telephone-input" type="tel" value={form.telephone} onChange={set("telephone")} placeholder="06 00 00 00 00" className={inputCls} />
              </div>
            </div>

            <div className="p-4 rounded bg-[#0A1628] border border-[#1C2C4E] flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-[#51ADDA] shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="5" width="20" height="14" rx="2" />
                  <path d="M2 10h20" strokeLinecap="round" />
                </svg>
                <span className="text-xs text-slate-400">Paiement sécurisé par <strong>carte bancaire</strong> après réception du devis validé à <span className="font-mono text-[#51ADDA]">bellahcen.youness@gmail.com</span>.</span>
              </div>
              <span className="text-xs font-mono text-[#51ADDA] uppercase shrink-0">France Entière</span>
            </div>

            {error && (
              <div data-testid="devis-error-alert" className="p-4 rounded bg-red-400/10 border border-red-400/30 text-red-400 text-sm">{error}</div>
            )}

            <button
              type="submit"
              data-testid="devis-submit-button"
              disabled={loading}
              className="w-full bg-[#EF3340] text-white font-display uppercase tracking-wider text-xl py-4 rounded hover:bg-[#f87171] transition-colors font-bold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Envoi en cours..." : "Envoyer ma demande de devis express"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
};
